import React from 'react';
import { AxiosError } from 'axios';
import { Typography } from '@one-thd/sui-atomic-components';
import { pushErrorEvent } from '../../../utils/analytics';
import { Alert } from '../../shared/alert/alert';
import { Card } from '../../shared/card/card';
import { getLastModifiedDate } from '../../../utils/date';
import {
  IPaymentCardCommonData,
  TPaymentErrorResponse,
} from '../../../models/common/payments';
import { MeatballsMenu } from '../../shared/meatballs-menu/meatballs-menu';
import { usePayments } from '../../../hooks/use-payments';
import { CardType } from '../../../constants/payments';
import { ERR_UNABLE_TO_SAVE } from '../../../constants/response-error-messages';
import {
  CHANGES_SAVED_MSG,
  DELETED_CARD_MSG,
  DELETED_KEY_TAG,
} from '../../../constants/response-success-messages';
import { IB2BPaymentCard } from '../../../models/b2b/payments';
import {
  getFormattedKeytag,
  getPaymentsErrorCode,
} from '../../../utils/payments';
import { useCustomerInfo } from '../../../hooks/use-customerInfo';
import { useMeatballMenuActions } from '../../../hooks/use-meatball-payment-actions';
import { CustomerRole } from '../../../models/common/customer';

interface IKeytagCardProps {
  card: IPaymentCardCommonData;
  index: number;
  isMobile: boolean;
  setDeletedCreditCardIndex: (index: number) => void;
  onActionSuccess: (paymentId: string, message: string) => void;
  onActionFailure: (paymentId: string, message: string) => void;
}

export const KeytagCard = ({
  card,
  isMobile,
  index,
  setDeletedCreditCardIndex,
  onActionFailure,
  onActionSuccess,
}: IKeytagCardProps) => {
  const { alertMessage, setAlertMessage } = usePayments();
  const { role } = useCustomerInfo();
  const { keytags } = usePayments();
  const { onEditNickName, onDelete } = useMeatballMenuActions();

  const isAdmin = role === CustomerRole.ADMIN;
  const isCardAddedByPurchaser = card.level === 'user';

  const isHiddenForAdmin = isAdmin && isCardAddedByPurchaser;

  const handleEditNickname = () => {
    const currentKeytag = (keytags?.response as Array<IB2BPaymentCard>).find(
      (payment) => payment.cardNumber === card.cardNumber,
    );

    const onSuccess = () => {
      onActionSuccess(card.cardNumber, CHANGES_SAVED_MSG.message);
    };

    const onFailure = (error: AxiosError<TPaymentErrorResponse>) => {
      const errorCode = getPaymentsErrorCode(error);
      onActionFailure(card.cardNumber, ERR_UNABLE_TO_SAVE.message);
      pushErrorEvent(
        'edit keytag',
        'inline',
        errorCode || 'n/a',
        ERR_UNABLE_TO_SAVE.message,
      );
    };

    onEditNickName(card, currentKeytag, CardType.KEY_TAG, onSuccess, onFailure);
  };

  const handleDeleteCard = () => {
    const onSuccess = () => {
      onActionSuccess(card.cardNumber, DELETED_KEY_TAG.message);
    };
    const onFailure = (error: AxiosError<TPaymentErrorResponse>) => {
      const errorCode = getPaymentsErrorCode(error);
      onActionFailure(card.cardNumber, ERR_UNABLE_TO_SAVE.message);
      pushErrorEvent(
        'keytag: delete',
        'inline',
        errorCode || 'n/a',
        ERR_UNABLE_TO_SAVE.message,
      );
    };
    onDelete(card, CardType.KEY_TAG, onSuccess, onFailure, () => {
      setDeletedCreditCardIndex(index);
    });
  };

  const menuItems = [
    {
      value: 'Add/Edit Nickname',
      action: handleEditNickname,
      isHidden: !card.actions?.edit,
    },
    {
      value: 'Delete',
      action: handleDeleteCard,
      isHidden: isHiddenForAdmin,
    },
  ];

  const isDeleteSuccessMessage =
    alertMessage?.text === DELETED_CARD_MSG.message;

  const mobileCardAlertMessage = isMobile &&
    alertMessage?.paymentId === card.cardNumber &&
    !isDeleteSuccessMessage && (
      <div className="sui-mb-4">
        <Alert
          status={alertMessage.alertStatus}
          onClose={() => setAlertMessage(null)}
        >
          {alertMessage.text}
        </Alert>
      </div>
    );

  return (
    <>
      {mobileCardAlertMessage}
      <Card isDefault={false} defaultLabel="Default Payment Method">
        <div
          className={`sui-flex sui-justify-between sui-align-center ${
            card.cardNickName ? 'sui-mb-2' : 'sui-mb-3'
          }`}
        >
          <h3
            className="sui-font-display sui-text-3xl sui-mr-4 sui-truncate sui-text-primary"
            aria-label="nickname"
          >
            {card.cardNickName}
          </h3>
          <div>
            <MeatballsMenu label="Payment" items={menuItems} />
          </div>
        </div>
        <div className="sui-mb-6 sui-whitespace-nowrap">
          <Typography
            variant="body-lg"
            color="subtle"
            data-testid="card number"
          >
            {getFormattedKeytag(card)}
          </Typography>
        </div>
        {card.addedBy && (
          <div className="sui-overflow-hidden">
            <Typography truncate color="subtle">
              Added by <span data-qm-mask="">{card.addedBy}</span>
            </Typography>
          </div>
        )}
        {card.lastModifiedDate && (
          <Typography color="subtle">
            Last edited {getLastModifiedDate(card.lastModifiedDate)}
          </Typography>
        )}
      </Card>
    </>
  );
};
KeytagCard.displayName = 'KeytagCard';
